import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Star, BadgeCheck, Calendar, MessageCircle, Heart } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { isGuest, trackGuest } from "../../store/guestStore";
import { payForBooking } from "../../services/razorpay";
import FloatingCTA from "../../components/layout/FloatingCTA";
import EnquirySheet from "./EnquirySheet";
import GuestBanner from "../../components/common/GuestBanner";
import RegistrationSheet from "../../components/common/RegistrationSheet";
import BottomNav from "../../components/layout/BottomNav";
import BookingCalendar from "../../components/trainers/BookingCalendar";
import MarketplaceStrip from "../../components/marketplace/MarketplaceStrip";
import { toast } from "sonner";

export default function TrainerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const [trainer, setTrainer] = useState(null);
  const [tier, setTier] = useState(0);
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showReg, setShowReg] = useState(false);
  const [regContext, setRegContext] = useState("trainer_enquiry");
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [introTier, setIntroTier] = useState(null); // { enabled, already_used, duration_minutes }
  const [introBusy, setIntroBusy] = useState(false);
  const guest = !user && isGuest();

  useEffect(() => {
    apiClient.get(`/trainers/${id}`).then((r) => setTrainer(r.data));
  }, [id]);

  // Fetch intro-tier state — professionals only, silent for legacy trainers.
  useEffect(() => {
    const trainerUid = trainer?.user_id || trainer?.application_id || trainer?.trainer_id;
    if (!trainerUid || !user) return;
    if (trainer?.source && trainer.source !== "professionals") return;
    apiClient
      .get(`/pro/intro-tier/${trainerUid}`)
      .then((r) => setIntroTier(r.data))
      .catch(() => setIntroTier(null));
  }, [trainer?.user_id, trainer?.application_id, trainer?.trainer_id, trainer?.source, user]);

  useEffect(() => {
    const trainerUid = trainer?.user_id || trainer?.trainer_id;
    if (!trainerUid) return;
    apiClient
      .get(`/marketplace/pro/${trainerUid}/recommended-products`)
      .then((r) => setRecommendedProducts(r.data?.products || []))
      .catch(() => setRecommendedProducts([]));
  }, [trainer?.user_id, trainer?.trainer_id]);

  if (!trainer) {
    return (
      <div className="min-h-screen grid place-items-center bg-canvas">
        <div className="text-ink-muted">Loading…</div>
      </div>
    );
  }

  const handleBook = async () => {
    if (!user) {
      toast.message("Please sign in to book a session", { description: "It's free, takes 30 seconds." });
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      await payForBooking({
        trainer_id: trainer.trainer_id,
        tier_index: tier,
        slot_day: slot?.day,
        slot_time: slot?.time,
      });
      toast.success("Payment received", {
        description: `Waiting for ${trainer.name} to confirm your session. +30 FitPoints.`,
      });
      setTimeout(() => navigate("/home"), 1500);
    } catch (e) {
      if (e.message !== "dismissed") {
        toast.error("Payment failed", { description: e.message });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas pb-32" data-testid="trainer-profile">
      {/* Hero */}
      <div className="relative h-[260px]">
        <img src={trainer.hero_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.7) 100%)" }} />
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 w-10 h-10 grid place-items-center rounded-full bg-white/85 backdrop-blur"
          aria-label="Back"
        >
          ‹
        </button>
        <div className="absolute bottom-5 left-5 right-5 text-white flex items-end gap-4">
          <img src={trainer.avatar_url} alt="" className="w-20 h-20 rounded-full border-2 border-white object-cover" />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h1 className="font-display text-[28px] leading-tight">{trainer.name}</h1>
              {trainer.is_verified && <BadgeCheck size={20} style={{ color: "var(--accent-blue)" }} />}
            </div>
            <div className="text-[13px] text-white/85 mt-0.5">{trainer.city} · {trainer.years_experience}+ years</div>
            <div className="flex items-center gap-1.5 text-[13px] text-white/90 mt-1">
              <Star size={14} fill="currentColor" /> {trainer.rating} ({trainer.review_count} reviews)
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 pt-6 space-y-6">
        {/* Bio */}
        <section className="bg-white rounded-[16px] p-5 border border-line2" style={{ boxShadow: "var(--shadow-soft)" }}>
          <h2 className="font-display text-[20px]">About</h2>
          <p className="text-[14.5px] text-ink-soft mt-2 leading-relaxed">{trainer.bio}</p>
        </section>

        {/* Specialties */}
        <section>
          <h2 className="font-display text-[20px] mb-3">Specialties</h2>
          <div className="flex flex-wrap gap-2">
            {trainer.specialties.map((s) => (
              <span key={s} className="px-3.5 h-9 rounded-pill bg-white border border-line text-[13px] grid place-items-center" style={{ borderRadius: 100 }}>{s}</span>
            ))}
          </div>
        </section>

        {/* Certifications */}
        {trainer.certifications?.length > 0 && (
          <section>
            <h2 className="font-display text-[20px] mb-3">Certifications</h2>
            <ul className="space-y-1.5">
              {trainer.certifications.map((c) => (
                <li key={c} className="flex items-center gap-2 text-[14px] text-ink-soft">
                  <BadgeCheck size={16} style={{ color: "var(--accent-blue)" }} /> {c}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Free intro call — subtle label, above Packages. Only shown when
            the pro has it enabled and the current member hasn't used it yet. */}
        {introTier?.enabled && !introTier?.already_used && (
          <section data-testid="intro-call-tile">
            <button
              onClick={async () => {
                if (!user) {
                  setRegContext("trainer_enquiry");
                  setShowReg(true);
                  return;
                }
                if (!slot) {
                  toast.message("Pick a slot above first", { description: "Then tap Book free intro." });
                  return;
                }
                setIntroBusy(true);
                try {
                  const trainerUid = trainer?.user_id || trainer?.application_id || trainer?.trainer_id;
                  // slot from availability grid → build session_at
                  const sessionAt = slot.session_at || `${slot.date || slot.day}T${slot.time}:00`;
                  const { data } = await apiClient.post("/pro/bookings/intro", {
                    trainer_user_id: trainerUid,
                    session_at: sessionAt,
                    method: "jitsi",
                  });
                  toast.success("Request sent", {
                    description: `Waiting for ${trainer.name} to confirm. Video link: ${data.meet_url}`,
                  });
                  setIntroTier({ ...introTier, already_used: true });
                  setTimeout(() => navigate("/home"), 1500);
                } catch (e) {
                  toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not book intro");
                } finally {
                  setIntroBusy(false);
                }
              }}
              disabled={introBusy}
              data-testid="book-intro-btn"
              className="w-full text-left p-4 rounded-[14px] border transition-all"
              style={{
                background: "#FFFFFF",
                borderColor: "var(--border)",
                boxShadow: "var(--shadow-soft)",
              }}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display text-[17px]">Free intro call</div>
                  <div className="text-[12px] text-ink-soft mt-0.5">
                    {introTier.duration_minutes} minutes · no charge · one per pro
                  </div>
                </div>
                <div className="font-display text-[18px]" style={{ color: "#0F766E" }}>₹0</div>
              </div>
            </button>
          </section>
        )}

        {/* Pricing tiers */}
        <section data-testid="pricing-tiers">
          <h2 className="font-display text-[20px] mb-3">Packages</h2>
          <div className="space-y-2">
            {trainer.pricing_tiers.map((p, i) => (
              <button
                key={p.name}
                onClick={() => setTier(i)}
                data-testid={`tier-${i}`}
                className="w-full text-left p-4 rounded-[14px] border-2 transition-all"
                style={{
                  background: "white",
                  borderColor: tier === i ? "var(--accent-blue)" : "var(--border)",
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display text-[18px]">{p.name}</div>
                    <div className="text-[12.5px] text-ink-soft mt-0.5">{p.description}</div>
                  </div>
                  <div className="font-display text-[22px]">₹{p.price_inr.toLocaleString("en-IN")}</div>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Availability */}
        {trainer.available_slots?.length > 0 && (
          <section>
            <h2 className="font-display text-[20px] mb-3">Pick a slot</h2>
            <div className="grid grid-cols-3 gap-2">
              {trainer.available_slots.map((s, i) => {
                const active = slot && slot.day === s.day && slot.time === s.time;
                return (
                  <button
                    key={i}
                    onClick={() => setSlot(s)}
                    data-testid={`slot-${i}`}
                    className="rounded-[12px] py-3 text-center text-[13px] transition-all border"
                    style={{
                      background: active ? "var(--accent-blue-dim)" : "white",
                      borderColor: active ? "var(--accent-blue)" : "var(--border-light)",
                      boxShadow: "var(--shadow-soft)",
                    }}
                  >
                    <div className="font-medium">{s.day}</div>
                    <div className="text-ink-soft text-[12px] mt-0.5 flex items-center justify-center gap-1">
                      <Calendar size={11} /> {s.time}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Send Enquiry — defined conversion mechanism for V3.0 spec */}
        <button
          onClick={() => {
            if (!user) {
              setRegContext("trainer_enquiry");
              trackGuest("soft_gate_shown", { context: "trainer_enquiry" });
              setShowReg(true);
              return;
            }
            setEnquiryOpen(true);
          }}
          data-testid="send-enquiry-btn"
          className="w-full rounded-[14px] p-4 flex items-center justify-center gap-2"
          style={{
            background: "#00D4FF",
            color: "#000",
            fontFamily: "Manrope",
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          Send Enquiry
        </button>

        {/* Save profile */}
        <button
          onClick={async () => {
            if (!user) return navigate("/login");
            try {
              if (saved) {
                await apiClient.delete(`/pro/saves/${trainer.application_id || trainer.trainer_id}`);
                setSaved(false);
                toast.success("Removed from saved");
              } else {
                await apiClient.post(`/pro/saves/${trainer.application_id || trainer.trainer_id}`);
                setSaved(true);
                toast.success("Saved to your list");
              }
            } catch {
              // Trainer marketplace also includes seeded mock trainers without
              // application_id — silently fail in that case
            }
          }}
          data-testid="save-profile-btn"
          className="w-full rounded-[14px] p-4 flex items-center justify-center gap-2 border border-line2"
        >
          <Heart size={16} fill={saved ? "#E5484D" : "none"} color={saved ? "#E5484D" : "currentColor"} />
          <span className="font-medium">{saved ? "Saved" : "Save Profile"}</span>
        </button>

        {/* Message */}
        <button
          onClick={() => toast.info("Send an enquiry first to start a thread")}
          className="w-full bg-white rounded-[14px] p-4 flex items-center justify-between border border-line2"
          style={{ boxShadow: "var(--shadow-soft)" }}
        >
          <span className="font-medium">Message {trainer.name.split(" ")[0]}</span>
          <MessageCircle size={18} className="text-ink-soft" />
        </button>

        {/* Trainer recommended products */}
        {recommendedProducts.length > 0 && (
          <MarketplaceStrip
            headline={`${(trainer.name || "Trainer").split(" ")[0]} recommends`}
            sub="Trainer-picked gear & supplements"
            products={recommendedProducts}
            shopHref="/marketplace"
            testid="trainer-recommends-strip"
            tag="Trainer recommends"
            trainerRef={trainer.user_id || trainer.trainer_id}
          />
        )}

        {/* Self-serve availability calendar */}
        <section className="bg-white rounded-[14px] p-4 border border-line2" data-testid="trainer-book-section">
          <h3 className="font-barlow-cond font-extrabold text-[20px] mb-3">Book a Session</h3>
          <BookingCalendar trainerUserId={trainer.user_id || trainer.trainer_id} trainerName={trainer.name} />
        </section>
      </main>

      {enquiryOpen && (
        <EnquirySheet
          professionalId={trainer.user_id || trainer.trainer_id}
          professionalName={trainer.name}
          onClose={() => setEnquiryOpen(false)}
        />
      )}

      <FloatingCTA
        label={
          busy
            ? "Opening checkout…"
            : `Book ${trainer.pricing_tiers[tier].name} · ₹${trainer.pricing_tiers[tier].price_inr.toLocaleString("en-IN")}`
        }
        onClick={() => {
          if (!user) {
            setRegContext("trainer_enquiry");
            trackGuest("soft_gate_shown", { context: "trainer_enquiry" });
            setShowReg(true);
            return;
          }
          handleBook();
        }}
        disabled={busy}
        testid="book-trainer-cta"
        hint={slot ? `Slot · ${slot.day} ${slot.time}` : "Pick a slot above to confirm your time"}
      />
      {guest && <GuestBanner onSignUp={() => { setRegContext("general"); setShowReg(true); }} />}
      {showReg && (
        <RegistrationSheet
          context={regContext}
          onSuccess={() => setShowReg(false)}
          onDismiss={() => setShowReg(false)}
        />
      )}
    </div>
  );
}
